import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  ArrowDown, ArrowUp, ChevronDown, ChevronRight, Clock, ListOrdered, Mail, Phone,
  Plus, Save, Trash2, X,
} from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';

const cardCls = 'rounded-lg border border-line bg-surface p-4';
const selectCls = 'h-10 w-full px-3 text-sm rounded-md border border-line bg-surface text-fg';
const err = (e, fallback) => toast.error(e?.response?.data?.detail || fallback);
const emptyTree = { name: '', description: '', is_active: true, steps: [] };
const emptyStep = { contact_id: '', label: '', instruction: '', wait_minutes: '' };

const tel = (n) => (n || '').replace(/[^\d+]/g, '');

/* ── Read view: what an operator works down during an event ────────────── */
function TreeSteps({ tree, index }) {
  if (!tree.steps?.length) {
    return (
      <p className="text-sm text-fg-muted" data-testid={`call-tree-empty-${index}`}>
        No steps yet — a supervisor still needs to fill in who to call for this event.
      </p>
    );
  }
  return (
    <ol className="space-y-2" data-testid={`call-tree-steps-${index}`}>
      {tree.steps.map((s, i) => {
        const c = s.contact;
        return (
          <li key={s.id || i} className="flex gap-3 rounded-md border border-line bg-surface-sunken p-3"
              data-testid={`call-tree-step-${index}-${i}`}>
            <span className="shrink-0 w-7 h-7 rounded-full bg-surface border border-line text-sm
                             font-semibold text-fg flex items-center justify-center">{i + 1}</span>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-fg">
                {c ? c.name : (s.label || 'Step')}
                {c?.title && <span className="font-normal text-fg-muted"> · {c.title}</span>}
                {c?.company && <span className="font-normal text-fg-muted"> · {c.company}</span>}
              </p>
              {c && (
                <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1">
                  {c.phone_primary && (
                    <a href={`tel:${tel(c.phone_primary)}`} className="flex items-center gap-1.5 text-sm text-fg hover:underline">
                      <Phone className="w-3.5 h-3.5 text-fg-muted" aria-hidden="true" />
                      <span className="font-medium">{c.phone_primary}</span>
                      <span className="text-xs text-fg-muted">{c.phone_primary_label}</span>
                    </a>
                  )}
                  {c.phone_secondary && (
                    <a href={`tel:${tel(c.phone_secondary)}`} className="flex items-center gap-1.5 text-sm text-fg hover:underline">
                      <Phone className="w-3.5 h-3.5 text-fg-muted" aria-hidden="true" />
                      <span className="font-medium">{c.phone_secondary}</span>
                      <span className="text-xs text-fg-muted">{c.phone_secondary_label}</span>
                    </a>
                  )}
                  {c.email && (
                    <a href={`mailto:${c.email}`} className="flex items-center gap-1.5 text-sm text-fg hover:underline">
                      <Mail className="w-3.5 h-3.5 text-fg-muted" aria-hidden="true" />{c.email}
                    </a>
                  )}
                </div>
              )}
              {s.instruction && <p className="text-sm text-fg mt-1 whitespace-pre-wrap">{s.instruction}</p>}
              {(s.wait_minutes ?? null) !== null && s.wait_minutes !== '' && (
                <p className="text-xs text-fg-muted mt-1 flex items-center gap-1">
                  <Clock className="w-3 h-3" aria-hidden="true" />
                  Wait {s.wait_minutes} min before the next step
                </p>
              )}
              {!c && s.contact_id && (
                <p className="text-xs text-danger mt-1">This contact is no longer in the directory.</p>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/* ── Editor ────────────────────────────────────────────────────────────── */
function TreeEditor({ tree, contacts, onCancel, onSaved }) {
  const [draft, setDraft] = useState({
    ...tree,
    steps: (tree.steps || []).map((s) => ({
      contact_id: s.contact_id || '', label: s.label || '',
      instruction: s.instruction || '', wait_minutes: s.wait_minutes ?? '',
    })),
  });

  const setStep = (i, patch) => setDraft((d) => ({
    ...d, steps: d.steps.map((s, ix) => (ix === i ? { ...s, ...patch } : s)),
  }));
  const move = (from, to) => setDraft((d) => {
    if (to < 0 || to >= d.steps.length) return d;
    const steps = [...d.steps];
    const [row] = steps.splice(from, 1);
    steps.splice(to, 0, row);
    return { ...d, steps };
  });

  const save = async () => {
    if (!draft.name.trim()) return toast.error('Give the call tree a name');
    const payload = {
      name: draft.name.trim(),
      description: draft.description?.trim() || null,
      is_active: !!draft.is_active,
      steps: draft.steps
        .filter((s) => s.contact_id || s.label?.trim() || s.instruction?.trim())
        .map((s) => ({
          contact_id: s.contact_id || null,
          label: s.label?.trim() || null,
          instruction: s.instruction?.trim() || null,
          wait_minutes: s.wait_minutes === '' ? null : Number(s.wait_minutes),
        })),
    };
    try {
      if (draft.id) await api.put(`/gsoc/call-trees/${draft.id}`, payload);
      else await api.post('/gsoc/call-trees', payload);
      toast.success('Call tree saved');
      onSaved();
    } catch (e) { err(e, 'Could not save the call tree'); }
  };

  return (
    <div className={`${cardCls} border-brand/40`} data-testid="call-tree-editor">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-fg">{draft.id ? 'Edit call tree' : 'New call tree'}</h3>
        <Button variant="ghost" size="sm" onClick={onCancel} data-testid="call-tree-editor-close">
          <X className="w-4 h-4" aria-hidden="true" />
        </Button>
      </div>

      <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="md:col-span-2">
          <Label className="text-sm">Event name</Label>
          <Input className="mt-1" value={draft.name} placeholder="e.g. Internet Loss — AT&T"
                 onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                 data-testid="call-tree-input-name" />
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Switch checked={!!draft.is_active}
                    onCheckedChange={(v) => setDraft((d) => ({ ...d, is_active: v }))}
                    data-testid="call-tree-input-active" />
            <span className="text-fg-muted">Visible to operators</span>
          </label>
        </div>
      </div>
      <div className="mt-3">
        <Label className="text-sm">When to use it</Label>
        <Textarea rows={2} className="mt-1" value={draft.description || ''}
                  placeholder="Describe the trigger in plain words, e.g. Utility power lost AND the generator did not start."
                  onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
                  data-testid="call-tree-input-description" />
      </div>

      <div className="mt-4 space-y-2">
        <Label className="text-sm">Steps, in the order they should be worked</Label>
        {draft.steps.map((s, i) => (
          <div key={i} className="rounded-md border border-line p-3" data-testid={`call-tree-step-row-${i}`}>
            <div className="flex items-start gap-2">
              <span className="shrink-0 w-6 h-6 mt-1 rounded-full bg-surface-sunken border border-line
                               text-xs font-semibold text-fg flex items-center justify-center">{i + 1}</span>
              <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-2">
                <select className={selectCls} value={s.contact_id}
                        onChange={(e) => setStep(i, { contact_id: e.target.value })}
                        data-testid={`call-tree-step-contact-${i}`}>
                  <option value="">— no contact record —</option>
                  {contacts.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}{c.company ? ` (${c.company})` : ''}
                    </option>
                  ))}
                </select>
                <Input value={s.label} placeholder="Or type who / what, e.g. AT&T support line"
                       onChange={(e) => setStep(i, { label: e.target.value })}
                       data-testid={`call-tree-step-label-${i}`} />
                <Input type="number" min="0" max="240" value={s.wait_minutes}
                       placeholder="Wait mins before next"
                       onChange={(e) => setStep(i, { wait_minutes: e.target.value })}
                       data-testid={`call-tree-step-wait-${i}`} />
                <Textarea rows={2} className="md:col-span-3" value={s.instruction}
                          placeholder="What to say or do at this step."
                          onChange={(e) => setStep(i, { instruction: e.target.value })}
                          data-testid={`call-tree-step-instruction-${i}`} />
              </div>
              <div className="flex flex-col gap-1">
                <Button variant="ghost" size="sm" className="px-2" disabled={i === 0}
                        onClick={() => move(i, i - 1)} title="Move up" data-testid={`call-tree-step-up-${i}`}>
                  <ArrowUp className="w-4 h-4" aria-hidden="true" />
                </Button>
                <Button variant="ghost" size="sm" className="px-2" disabled={i === draft.steps.length - 1}
                        onClick={() => move(i, i + 1)} title="Move down" data-testid={`call-tree-step-down-${i}`}>
                  <ArrowDown className="w-4 h-4" aria-hidden="true" />
                </Button>
                <Button variant="ghost" size="sm" className="px-2" title="Remove step"
                        onClick={() => setDraft((d) => ({ ...d, steps: d.steps.filter((_, ix) => ix !== i) }))}
                        data-testid={`call-tree-step-remove-${i}`}>
                  <Trash2 className="w-4 h-4" aria-hidden="true" />
                </Button>
              </div>
            </div>
          </div>
        ))}
        <Button variant="outline" size="sm"
                onClick={() => setDraft((d) => ({ ...d, steps: [...d.steps, { ...emptyStep }] }))}
                data-testid="call-tree-add-step-btn">
          <Plus className="w-4 h-4 mr-1" aria-hidden="true" />Add Step
        </Button>
      </div>

      <div className="mt-4 flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={save} data-testid="call-tree-save-btn">
          <Save className="w-4 h-4 mr-1" aria-hidden="true" />Save Call Tree
        </Button>
      </div>
    </div>
  );
}

/* ── List ──────────────────────────────────────────────────────────────── */
export function CallTrees({ contacts, canEdit, canDelete }) {
  const [trees, setTrees] = useState([]);
  const [open, setOpen] = useState({});
  const [editing, setEditing] = useState(null);

  const load = () => api.get('/gsoc/call-trees', { params: { include_inactive: canEdit } })
    .then(({ data }) => setTrees(data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [canEdit]);

  const remove = async (t) => {
    try { await api.delete(`/gsoc/call-trees/${t.id}`); toast.success('Call tree deleted'); load(); }
    catch (e) { err(e, 'Could not delete the call tree'); }
  };

  const move = async (from, to) => {
    if (to < 0 || to >= trees.length) return;
    const next = [...trees];
    const [row] = next.splice(from, 1);
    next.splice(to, 0, row);
    setTrees(next);
    try { await api.post('/gsoc/call-trees/reorder', { ids: next.map((t) => t.id) }); }
    catch (e) { err(e, 'Could not reorder'); load(); }
  };

  return (
    <div className="space-y-3" data-testid="call-trees-panel">
      {canEdit && !editing && (
        <Button onClick={() => setEditing({ ...emptyTree })} data-testid="call-tree-new-btn">
          <Plus className="w-4 h-4 mr-1" aria-hidden="true" />New Call Tree
        </Button>
      )}
      {editing && (
        <TreeEditor tree={editing} contacts={contacts}
                    onCancel={() => setEditing(null)}
                    onSaved={() => { setEditing(null); load(); }} />
      )}

      {trees.length === 0 && (
        <p className="text-sm text-fg-muted" data-testid="call-trees-empty">No call trees yet.</p>
      )}

      {trees.map((t, i) => (
        <div key={t.id} className={cardCls} data-testid={`call-tree-${i}`}>
          <div className="flex items-start justify-between gap-3">
            <button type="button" className="flex items-start gap-2 text-left min-w-0"
                    onClick={() => setOpen((s) => ({ ...s, [t.id]: !s[t.id] }))}
                    data-testid={`call-tree-toggle-${i}`}>
              {open[t.id] ? <ChevronDown className="w-4 h-4 mt-1 shrink-0" aria-hidden="true" />
                          : <ChevronRight className="w-4 h-4 mt-1 shrink-0" aria-hidden="true" />}
              <span className="min-w-0">
                <span className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-fg">{t.name}</span>
                  <Badge className="bg-surface-sunken text-fg-muted border border-line">
                    <ListOrdered className="w-3 h-3 mr-1" aria-hidden="true" />
                    {t.steps?.length || 0} {(t.steps?.length || 0) === 1 ? 'step' : 'steps'}
                  </Badge>
                  {!t.is_active && <Badge className="bg-surface-sunken text-fg-muted border border-line">hidden</Badge>}
                </span>
                {t.description && <span className="block text-sm text-fg-muted mt-0.5">{t.description}</span>}
              </span>
            </button>
            <div className="flex items-center gap-1 shrink-0">
              {canEdit && (
                <>
                  <Button variant="ghost" size="sm" className="px-2" disabled={i === 0}
                          onClick={() => move(i, i - 1)} title="Move up" data-testid={`call-tree-up-${i}`}>
                    <ArrowUp className="w-4 h-4" aria-hidden="true" />
                  </Button>
                  <Button variant="ghost" size="sm" className="px-2" disabled={i === trees.length - 1}
                          onClick={() => move(i, i + 1)} title="Move down" data-testid={`call-tree-down-${i}`}>
                    <ArrowDown className="w-4 h-4" aria-hidden="true" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setEditing(t)} data-testid={`call-tree-edit-${i}`}>
                    Edit
                  </Button>
                </>
              )}
              {canDelete && (
                <Button variant="ghost" size="sm" onClick={() => remove(t)} data-testid={`call-tree-delete-${i}`}>
                  <Trash2 className="w-4 h-4" aria-hidden="true" />
                </Button>
              )}
            </div>
          </div>
          {open[t.id] && <div className="mt-3"><TreeSteps tree={t} index={i} /></div>}
        </div>
      ))}
    </div>
  );
}
